// Generic dropdown service that can handle multiple modules
export interface DropdownConfig {
  module: string;
  dataSource: 'json' | 'api' | 'static';
  source?: string; // file path or API endpoint
  transform?: (data: any) => any;
}

class DropdownService {
  private cache = new Map<string, any>();
  private configs: Record<string, DropdownConfig> = {};

  // Register module configurations
  register(configs: Record<string, DropdownConfig>) {
    this.configs = { ...this.configs, ...configs };
  }

  // Get dropdowns for a specific module
  async getForModule(module: string, categories?: string[]): Promise<any> {
    const config = this.configs[module];
    if (!config) {
      console.error(`❌ Module '${module}' not configured.`, {
        "module (module name)": module,
        "categories (requested categories)": categories,
        "availableModules (available modules)": Object.keys(this.configs),
      });
      throw new Error(`Module '${module}' not configured`);
    }

    const cacheKey = `${module}_${categories?.join(',') || 'all'}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }
    let data: any;
    
    switch (config.dataSource) {
      case 'json':
        data = await this.loadFromJson(config.source!);
        break;
      case 'api':
        data = await this.loadFromApi(config.source!);
        break;
      case 'static':
        data = await this.loadStatic(module);
        break;
    }

    if (config.transform) {
      data = config.transform(data);
    }

    // Filter by categories if specified
    if (categories && categories.length > 0) {
      const filtered: Record<string, any> = {};
      categories.forEach((cat: string) => {
        filtered[cat] = data[cat] || [];
      });
      data = filtered;
    }

    this.cache.set(cacheKey, data);
    return data;
  }

  private async loadFromJson(source: string) {
    // Handle different path formats
    let resolvedSource = source;
    if (source.startsWith('@/')) {
      // Convert @/ alias to relative path for dynamic imports
      resolvedSource = source.replace('@/', '../');
    }
    
    try {
      const importedModule = await import(resolvedSource);
      const data = importedModule.default || importedModule;
      return data;
    } catch (importError) {
      console.error('❌ Failed to import from resolved path:', {
        "source (config source)": source,
        "resolvedSource (resolved import path)": resolvedSource,
        "importError (import error)": importError,
      });
      throw importError;
    }
  }

  private async loadFromApi(endpoint: string) {
    const response = await fetch(endpoint);
    if (!response.ok) {
      throw new Error(`HTTP error. status: ${response.status}`);
    }
    return response.json();
  }

  private async loadStatic(module: string) {
    // Return hardcoded static data for specific modules
    return this.getStaticData(module);
  }

  private getStaticData(module: string) {
    const staticData: Record<string, any> = {
      'product-related': {
        qaManagers: [
          { id: 1, label: "Jane Smith", value: "janeSmith" },
          { id: 2, label: "John Doe", value: "johnDoe" }
        ]
      }
    };
    return staticData[module] || {};
  }

  // Clear cache for a specific module or all
  clearCache(module?: string) {
    if (module) {
      const keysToDelete = Array.from(this.cache.keys()).filter(key => key.startsWith(module));
      keysToDelete.forEach(key => this.cache.delete(key));
      console.log(`🧹 Cleared cache for module: ${module}`);
    } else {
      this.cache.clear();
      console.log(`🧹 Cleared all dropdown cache`);
    }
  }
}

export const dropdownService = new DropdownService();
export default dropdownService;